import React, { useState, useEffect } from "react";
import { X, Search, MoreHorizontal, Filter } from "lucide-react";
import { Popover } from "./Popover";
import { Combobox } from "./Combobox";
import { VictimTargetFilter } from "./VictimTargetFilter";
import { KeywordSearch } from "./KeywordSearch";
import { MoreFiltersDrawer } from "./MoreFiltersDrawer";
import { fetchCategories, fetchSources, fetchTargets } from "../../api/leaks";
import { useAuth } from "../../auth/AuthContext";

export interface FilterState {
  category?: string;
  threatActor?: string;
  targets?: string; // JSON string Record<string, string[]>
  search?: string;
  includeContent?: boolean;
  sector?: string;
  network?: string;
  severity?: string;
  reviewStatus?: string;
}

interface FeedFiltersProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  onClear: () => void;
  hideSearch?: boolean; // For Saved Posts
  hasUnapplied?: boolean;
  setHasUnapplied?: (v: boolean) => void;
}

const TARGET_TYPES = [
  { id: "Country", label: "Country" },
  { id: "Industry", label: "Industry" },
  { id: "Organization", label: "Organization" },
  { id: "Site", label: "Site" },
  { id: "Region", label: "Region" },
];

export function FeedFilters({ filters, onChange, onClear, hideSearch = false, hasUnapplied, setHasUnapplied }: FeedFiltersProps) {
  const { user } = useAuth();
  
  // State for facets
  const [categories, setCategories] = useState<{id: string, label: string, count: number}[]>([]);
  const [sources, setSources] = useState<{id: string, label: string, count: number}[]>([]);
  const [targets, setTargets] = useState<{id: string, label: string, count: number}[]>([]);
  
  const [loadingTargets, setLoadingTargets] = useState(false);
  
  // Fetch facets
  useEffect(() => {
    fetchCategories({
      network: filters.network,
      sector: filters.sector,
      target_type: filters.targetType,
      target_value: filters.targetValue
    }).then(res => setCategories(res.map((c: any) => ({ ...c, label: c.id }))));
  }, [filters.network, filters.sector, filters.targetType, filters.targetValue]);
  
  useEffect(() => {
    fetchSources({
      category: filters.category,
      network: filters.network,
      sector: filters.sector,
      target_type: filters.targetType,
      target_value: filters.targetValue
    }).then(setSources);
  }, [filters.category, filters.network, filters.sector, filters.targetType, filters.targetValue]);
  
  useEffect(() => {
    if (filters.targetType) {
      setLoadingTargets(true);
      fetchTargets({
        type: filters.targetType,
        category: filters.category,
        forum: filters.threatActor,
        network: filters.network,
        sector: filters.sector
      }).then(setTargets).finally(() => setLoadingTargets(false));
    } else {
      setTargets([]);
    }
  }, [filters.targetType, filters.category, filters.threatActor, filters.network, filters.sector]);

  const hasFilters = Object.values(filters).some(v => v !== undefined && v !== "" && v !== false);

  const [localSearch, setLocalSearch] = useState(filters.search || "");
  useEffect(() => { setLocalSearch(filters.search || "") }, [filters.search]);

  return (
    <div className="flex flex-col mb-5">
      <div className="text-[12px] text-slate-500 font-bold tracking-wide uppercase mb-3">
        FILTERS
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[200px_200px_240px_1fr_minmax(120px,auto)] gap-4 mb-3">
        {/* Category */}
        <div className="relative">
          <Combobox
            items={categories}
            selectedIds={new Set(filters.category ? filters.category.split(",") : [])}
            onSelect={(id) => {
              const current = new Set(filters.category ? filters.category.split(",") : []);
              if (current.has(id)) current.delete(id); else current.add(id);
              onChange({ ...filters, category: Array.from(current).join(",") || undefined });
            }}
            onClearAll={() => onChange({ ...filters, category: undefined })}
            placeholder="Category"
            searchPlaceholder="Search categories..."
          />
        </div>

        {/* Threat Actor (Sources) */}
        <div className="relative">
          <Combobox
            items={sources}
            selectedIds={new Set(filters.threatActor ? filters.threatActor.split(",") : [])}
            onSelect={(id) => {
              const current = new Set(filters.threatActor ? filters.threatActor.split(",") : []);
              if (current.has(id)) current.delete(id); else current.add(id);
              onChange({ ...filters, threatActor: Array.from(current).join(",") || undefined });
            }}
            onClearAll={() => onChange({ ...filters, threatActor: undefined })}
            placeholder="Threat actor"
            searchPlaceholder="Search actors..."
          />
        </div>

        {/* Victim / Target */}
        <div className="relative">
          <VictimTargetFilter 
            targets={filters.targets ? JSON.parse(filters.targets) : {}} 
            onChange={(newTargets) => {
              const str = Object.keys(newTargets).length > 0 ? JSON.stringify(newTargets) : undefined;
              onChange({ ...filters, targets: str });
            }}
            onClear={() => onChange({ ...filters, targets: undefined })}
          />
        </div>

        {/* Keyword Search */}
        {!hideSearch ? (
          <KeywordSearch 
            filters={filters} 
            onChange={onChange} 
            localSearch={localSearch} 
            setLocalSearch={setLocalSearch} 
            setHasUnapplied={setHasUnapplied}
          />
        ) : <div />}

        {/* Search Button */}
        {!hideSearch ? (
          <button 
            className={`h-12 px-4 text-sm font-medium text-white rounded-lg transition-colors flex items-center justify-center gap-2 ${hasUnapplied ? "bg-indigo-500 hover:bg-indigo-400" : "bg-indigo-500/80 hover:bg-indigo-500"}`}
            onClick={() => onChange({ ...filters, search: localSearch || undefined })}
          >
            Search
            {hasUnapplied && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
          </button>
        ) : <div />}
      </div>

      {/* Row 2 */}
      <div className="flex items-center justify-between h-8">
        <div className="flex items-center gap-6 w-full justify-between">
          <div className="flex items-center">
            <button
              disabled={!hasFilters}
              onClick={() => {
                setLocalSearch("");
                onClear();
              }}
              className={`flex items-center gap-1.5 text-sm transition-colors ${hasFilters ? "text-slate-400 hover:text-white" : "text-slate-600 cursor-not-allowed"}`}
            >
              <X size={14} /> Clear filters
            </button>
          </div>
          
          <MoreFiltersDrawer filters={filters} onChange={onChange} />
        </div>
      </div>
    </div>
  );
}
